import { useQueryClient } from '@tanstack/react-query';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';

// ── API response shapes ──────────────────────────────────────────────────────

export interface PreLiveDataClearResult {
  logId: number;
  totalRowsDeleted: number;
  durationMs: number;
}

export interface PreLiveDataClearLogEntry {
  id: number;
  organisationId: number;
  initiatedByUserId: number;
  status: string;
  startedAtUtc: string;
  completedAtUtc: string | null;
  durationMs: number | null;
  errorMessage: string | null;
  totalRowsDeleted: number | null;
  detailJson: string | null;
}

// ── Demo / mock data ─────────────────────────────────────────────────────────

const MOCK_CLEAR_RESULT: PreLiveDataClearResult = {
  logId: 1,
  totalRowsDeleted: 0,
  durationMs: 1200,
};

const MOCK_HISTORY: PreLiveDataClearLogEntry[] = [];

// ── Mutation ─────────────────────────────────────────────────────────────────

export function useClearPreLiveData() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<PreLiveDataClearResult, Error, void>(
    async () => {
      return apiPost<PreLiveDataClearResult>('/api/v1/system/pre-live-data/clear');
    },
    async () => {
      return MOCK_CLEAR_RESULT;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
      },
    },
  );
}

// ── Query (history) ──────────────────────────────────────────────────────────

export function useClearPreLiveDataHistory() {
  const { apiGet } = useApi();

  return useModeAwareQuery<PreLiveDataClearLogEntry[], Error>(
    ['pre-live-data-clear-history'],
    async ({ signal }) => {
      return apiGet<PreLiveDataClearLogEntry[]>('/api/v1/system/pre-live-data/clear/history', undefined, signal);
    },
    MOCK_HISTORY,
    {
      staleTime: 30 * 1000,
    },
  );
}
